/**
 * Snapshot undo/redo history (build-plan §5.3 / D5).
 *
 * History is a bounded stack of `{order, sectionsById, selectedSectionId, selectedBlockId}` snapshots —
 * NOT an inverse-command log. Rationale (build-plan §5.3): documents are small (tens of sections), and
 * Immer's structural sharing means untouched section subtrees are the SAME object reference across
 * snapshots, so a 50-frame stack is kilobytes, and snapshot-undo is provably correct without
 * hand-rolling 10 inverse ops. Selection travels WITH the document so undoing an `insertSection`
 * doesn't leave a now-missing section selected and undoing a `removeSection` re-selects the right node.
 *
 * Coalescing: consecutive `updateSectionSettings`/`updateBlockSettings` on the SAME target within
 * `COALESCE_MS` collapse into ONE frame (keyed by `mergeKey = action+id+fieldKeys`) so one undo reverts
 * the whole typing/slider burst. STRUCTURAL ops never coalesce — each is its own undo entry.
 */
import type { SectionInstance, ThemeSettings } from '@aquilixshop/ui-react';

export const HISTORY_LIMIT = 50;
export const COALESCE_MS = 600;

export interface Snapshot {
  order: string[];
  sectionsById: Record<string, SectionInstance>;
  selectedSectionId: string | null;
  selectedBlockId: string | null;
  /**
   * The global theme settings (feature 0019). Theme edits go through their OWN versionless save channel
   * (they never dirty the page), but they DO live on the single undo/redo timeline: a snapshot carries
   * the whole `themeSettings` object by reference (a new object is minted per edit, so distinct versions
   * are tiny + immutable). Undo/redo restore it and rebuild the render context (builderStore).
   */
  themeSettings: ThemeSettings;
}

/** The subset of the store a snapshot captures + the coalescing bookkeeping it needs. */
export interface HistoryHost {
  order: string[];
  sectionsById: Record<string, SectionInstance>;
  selectedSectionId: string | null;
  selectedBlockId: string | null;
  themeSettings: ThemeSettings;
  past: Snapshot[];
  future: Snapshot[];
  /** Internal coalescing markers (not part of the persisted document). */
  _lastMergeKey: string | null;
  _lastEditAt: number;
}

/**
 * Capture the current document + selection. `order`/`sectionsById` entries are shared by reference with
 * the live state (Immer structural sharing), so this allocates only the two container objects, not the
 * sections inside them.
 */
export function snapshot(s: HistoryHost): Snapshot {
  return {
    order: s.order.slice(),
    sectionsById: { ...s.sectionsById },
    selectedSectionId: s.selectedSectionId,
    selectedBlockId: s.selectedBlockId,
    // Shared by reference — a theme edit mints a fresh object, so snapshots never alias a mutated one.
    themeSettings: s.themeSettings,
  };
}

/**
 * Push a PRE-mutation snapshot onto `past` (called by `commit()` from inside the Immer producer, with
 * `pre` captured from the COMMITTED state BEFORE the producer ran — never a draft reference, which Immer
 * would finalize to the post-mutation value).
 *
 * Coalescing: when `mergeKey` matches the previous edit's key AND the previous edit was within
 * `COALESCE_MS`, we SKIP pushing a new frame (the prior frame already captures the pre-burst state) —
 * so the whole burst collapses to one undo entry. A structural op passes `mergeKey: undefined`, which
 * always pushes and resets the merge marker.
 */
export function pushHistory(
  s: HistoryHost,
  mergeKey: string | undefined,
  pre: Snapshot,
  now = Date.now(),
): void {
  const coalesce =
    mergeKey != null && mergeKey === s._lastMergeKey && now - s._lastEditAt < COALESCE_MS;

  if (!coalesce) {
    s.past.push(pre);
    if (s.past.length > HISTORY_LIMIT) s.past.shift(); // drop the oldest frame
  }

  s._lastMergeKey = mergeKey ?? null;
  s._lastEditAt = now;
}

/**
 * Restore a snapshot into the live draft (used by undo/redo). Does NOT touch history stacks. Restores
 * `themeSettings` too; the caller (builderStore undo/redo) rebuilds the render context + persists the
 * theme via its dedicated channel when the restored theme differs from the current one.
 */
export function applySnapshot(s: HistoryHost, snap: Snapshot): void {
  s.order = snap.order.slice();
  s.sectionsById = { ...snap.sectionsById };
  s.selectedSectionId = snap.selectedSectionId;
  s.selectedBlockId = snap.selectedBlockId;
  s.themeSettings = snap.themeSettings;
}
